import { apiClient, baseUrl, downloadServerPdf } from './api.js';
import { unwrap } from './api-error.js';
import type { ApiPaths } from '@reos/api-client';

/**
 * Doklady pro klienty a katalog sluzeb (krok FAK2): typovany klient nad
 * `/v1/finance/client-invoices/*` a `/v1/finance/catalog/*`. Odpovedi jsou
 * `zod.any()` (viz `packages/finance/src/public/plugin.ts`), DTO typy jsou
 * proto psane rucne podle `plugin.ts` (invoiceToDto/catalogItemToDto);
 * request body chodi z generovaneho OpenAPI kontraktu - stejny vzor jako
 * `billing-api.ts` (FAK1).
 */

export type InvoiceStatus = 'draft' | 'issued' | 'paid' | 'credit_noted' | 'cancelled' | 'credit_note';
export type InvoiceSource = 'free' | 'commission';

export interface CatalogItemDto {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly unit: string;
  readonly defaultUnitPrice: string;
  readonly vatRatePercent: string;
  readonly active: boolean;
}

export interface InvoiceCustomerDto {
  readonly name: string;
  readonly ico: string | null;
  readonly dic: string | null;
  readonly street: string;
  readonly city: string;
  readonly zip: string;
  readonly email: string | null;
}

export interface InvoiceItemDto {
  readonly id: string;
  readonly catalogItemId: string | null;
  readonly description: string;
  readonly quantity: string;
  readonly unitPriceWithoutVat: string;
  readonly vatRatePercent: string;
}

export interface InvoiceDto {
  readonly id: string;
  readonly supplierId: string;
  readonly brokerUserId: string | null;
  readonly partyId: string | null;
  readonly caseId: string | null;
  readonly source: InvoiceSource;
  readonly status: InvoiceStatus;
  readonly currency: string;
  readonly language: 'cs' | 'en';
  readonly issueDate: string;
  readonly dueDate: string;
  readonly customer: InvoiceCustomerDto;
  readonly subtotalAmount: string;
  readonly vatAmount: string;
  readonly totalAmount: string;
  readonly note: string | null;
  /**
   * Krok TOKYFIX4 bod 1 — PREDMET DOKLADU (popis prvni polozky). Koncept cislo
   * z ucetnictvi jeste nema, takze predmet je v seznamu jedine, cim se dva
   * koncepty tehoz odberatele od sebe lisi. Nepovinny: starsi odpoved API
   * (nebo atrapa v testu) ho nenese.
   */
  readonly subject?: string | null;
  readonly myuctoNumber: string | null;
  readonly parentInvoiceId: string | null;
  readonly paidAt: string | null;
  readonly paidAmount: string | null;
  readonly sentAt: string | null;
  readonly sentTo: readonly string[] | null;
  readonly cancelReason: string | null;
  readonly createdAt: string;
}

export interface InvoiceWithItemsDto extends InvoiceDto {
  readonly items: readonly InvoiceItemDto[];
}

export interface CaseInvoiceContextDto {
  readonly caseId: string;
  readonly title: string;
  /** Krok UXP5 bod 7: nemovitost zakazky — z jejiho `asking_price` se bere zaklad provize. */
  readonly propertyId: string | null;
  readonly participants: ReadonlyArray<{ readonly partyId: string; readonly role: string }>;
}

/** Zrcadlo `CustomerPrefillSource` z `@reos/finance` (web-admin kontext v zavislostech nema). */
export type CustomerPrefillSourceDto = 'party-card' | 'party-contact' | 'party-tax-id';

export interface PartyPrefillDto {
  readonly name: string;
  readonly email: string | null;
  /** Krok FAK1 bod 1: adresa a IČO/DIČ z adresare — bez nich padalo vystaveni na 422. */
  readonly street: string;
  readonly city: string;
  readonly zip: string;
  readonly ico: string | null;
  readonly dic: string | null;
  readonly sources: Readonly<
    Record<'name' | 'email' | 'address' | 'ico' | 'dic', CustomerPrefillSourceDto | null>
  >;
}

export type CreateCatalogItemBody =
  ApiPaths['/v1/finance/catalog']['post']['requestBody']['content']['application/json'];
export type UpdateCatalogItemBody =
  ApiPaths['/v1/finance/catalog/{id}']['put']['requestBody']['content']['application/json'];
export type CreateFreeInvoiceBody =
  ApiPaths['/v1/finance/client-invoices']['post']['requestBody']['content']['application/json'];
export type CreateCommissionInvoiceBody =
  ApiPaths['/v1/finance/client-invoices/commission']['post']['requestBody']['content']['application/json'];
export type ListInvoicesQuery = NonNullable<
  ApiPaths['/v1/finance/client-invoices']['get']['parameters']['query']
>;
/** Krok FAK5 bod 2 - polozka, kterou chce uzivatel dobropisovat (castecne nebo cele). */
export type CreditNoteItem = { readonly itemId: string; readonly quantity: string };

/** Krok FAK5 bod 1 - stav uhrad z MyUcta (parovani plateb), ctene bez zmeny stavu. */
export interface PaymentsSnapshotDto {
  readonly payments: ReadonlyArray<{
    readonly paymentId: string;
    readonly amount: string;
    readonly paidOn: string;
    readonly variableSymbol: string | null;
    readonly bankReference: string | null;
    readonly source: string;
  }>;
  readonly paidTotal: string;
  readonly amountToPay: string;
  readonly remaining: string;
  readonly paymentStatus: 'unpaid' | 'partially_paid' | 'paid' | 'overpaid' | null;
}

export async function listCatalogItems(includeInactive = false): Promise<CatalogItemDto[]> {
  return (await unwrap(
    apiClient.GET('/v1/finance/catalog', { params: { query: { includeInactive } } }),
  )) as CatalogItemDto[];
}

export async function createCatalogItem(body: CreateCatalogItemBody): Promise<CatalogItemDto> {
  return (await unwrap(apiClient.POST('/v1/finance/catalog', { body }))) as CatalogItemDto;
}

export async function updateCatalogItem(id: string, body: UpdateCatalogItemBody): Promise<CatalogItemDto> {
  return (await unwrap(apiClient.PUT('/v1/finance/catalog/{id}', { params: { path: { id } }, body }))) as CatalogItemDto;
}

export async function listInvoices(query: ListInvoicesQuery = {}): Promise<InvoiceDto[]> {
  return (await unwrap(apiClient.GET('/v1/finance/client-invoices', { params: { query } }))) as InvoiceDto[];
}

export async function getInvoice(id: string): Promise<InvoiceWithItemsDto> {
  return (await unwrap(
    apiClient.GET('/v1/finance/client-invoices/{id}', { params: { path: { id } } }),
  )) as InvoiceWithItemsDto;
}

export async function deleteInvoiceDraft(id: string): Promise<void> {
  await unwrap(apiClient.DELETE('/v1/finance/client-invoices/{id}', { params: { path: { id } } }));
}

export async function createFreeInvoice(body: CreateFreeInvoiceBody): Promise<InvoiceWithItemsDto> {
  return (await unwrap(apiClient.POST('/v1/finance/client-invoices', { body }))) as InvoiceWithItemsDto;
}

export async function createCommissionInvoice(body: CreateCommissionInvoiceBody): Promise<InvoiceWithItemsDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/client-invoices/commission', { body }),
  )) as InvoiceWithItemsDto;
}

export async function getCaseInvoiceContext(caseId: string): Promise<CaseInvoiceContextDto> {
  return (await unwrap(
    apiClient.GET('/v1/finance/client-invoices/case/{caseId}/context', { params: { path: { caseId } } }),
  )) as CaseInvoiceContextDto;
}

export async function prefillCustomerFromParty(partyId: string): Promise<PartyPrefillDto> {
  return (await unwrap(
    apiClient.GET('/v1/finance/client-invoices/party/{partyId}/prefill', { params: { path: { partyId } } }),
  )) as PartyPrefillDto;
}

export async function issueInvoice(id: string): Promise<InvoiceWithItemsDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/client-invoices/{id}/issue', { params: { path: { id } } }),
  )) as InvoiceWithItemsDto;
}

export async function sendInvoiceEmail(id: string, body: { readonly to?: string[]; readonly note?: string } = {}): Promise<InvoiceWithItemsDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/client-invoices/{id}/send', { params: { path: { id } }, body }),
  )) as InvoiceWithItemsDto;
}

export async function markInvoicePaid(id: string, paidOn?: string): Promise<InvoiceWithItemsDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/client-invoices/{id}/mark-paid', { params: { path: { id } }, body: paidOn !== undefined ? { paidOn } : {} }),
  )) as InvoiceWithItemsDto;
}

/** `items` chybi = cely dobropis; s `items` = castecny (jen zvolene polozky v zadanem mnozstvi). */
export async function issueCreditNote(id: string, reason: string, items?: readonly CreditNoteItem[]): Promise<InvoiceWithItemsDto> {
  return (await unwrap(
    apiClient.POST('/v1/finance/client-invoices/{id}/credit-note', {
      params: { path: { id } },
      body: items !== undefined ? { reason, items: items as CreditNoteItem[] } : { reason },
    }),
  )) as InvoiceWithItemsDto;
}

/** Krok FAK5 bod 1 - precte stav uhrad z MyUcta; pri "paid"/"overpaid" doklad lokalne oznaci uhrazeny. */
export async function syncInvoicePayment(id: string): Promise<{ readonly invoice: InvoiceWithItemsDto; readonly payments: PaymentsSnapshotDto }> {
  return (await unwrap(
    apiClient.POST('/v1/finance/client-invoices/{id}/payments/sync', { params: { path: { id } } }),
  )) as { invoice: InvoiceWithItemsDto; payments: PaymentsSnapshotDto };
}

/**
 * Stazeni PDF dokladu. Telo zije v `downloadServerPdf` v `api.ts` — tutez
 * cestu ma `downloadContractPdf` (krok QAFIX7; drive dve shodne kopie).
 */
export async function downloadInvoicePdf(id: string, filename: string): Promise<void> {
  await downloadServerPdf(`${baseUrl}/v1/finance/client-invoices/${id}/pdf`, filename);
}
